import React, { useState, useEffect, useCallback } from 'react';
import { TextField, FormControl, InputLabel, Select, MenuItem, Box } from '@mui/material';
import SSHKeyConfiguration from './SSHKeyConfiguration';
import PATConfiguration from './PATConfiguration';
import { useTenants } from '../hooks/useTenants';
import { useCreateSSHConnection, useCreatePATConnection, useUpdateGitHubConnection, useDeleteGitHubConnection } from '../hooks/useGitHubConnection';

interface GithubSshConfig {
    tenantId: string;
    name: string;
    description?: string;
    repositoryUrl: string;
    accessMethod: 'ssh' | 'pat';
    connectionType?: 'repository' | 'organization' | 'user';
    keyType?: 'rsa' | 'ed25519';
    isActive: boolean;
    secretArn?: string;
    publicKey?: string;
    createdAt: string;
    updatedAt: string;
}

interface GitHubInstallation {
    connectionId: string;
    creationDate: string;
    installationId: string;
    accountLogin: string;
    accountType: 'User' | 'Organization';
    repositorySelection: 'all' | 'selected';
    permissions: any;
    isActive: boolean;
    ownerEmail: string;
    name: string;
    description?: string;
    updatedAt: string;
}

interface GitHubConnectionEditProps {
    connection?: GithubSshConfig;
    accessMethod?: 'ssh' | 'pat';
    installations?: GitHubInstallation[];
    onSave?: (connection: Partial<GithubSshConfig>) => void;
    onCancel: () => void;
    editorActions?: any;
    setEditorState?: (state: any) => void;
}

const GitHubConnectionEdit: React.FC<GitHubConnectionEditProps> = ({
    connection,
    accessMethod: initialAccessMethod,
    installations = [],
    onSave,
    onCancel,
    editorActions,
    setEditorState
}) => {
    const [formData, setFormData] = useState({
        tenantId: '',
        name: '',
        description: '',
        repositoryUrl: '',
        accessMethod: (initialAccessMethod || 'ssh') as 'ssh' | 'pat',
        connectionType: 'repository' as 'repository' | 'organization' | 'user',
        keyType: 'ed25519' as 'rsa' | 'ed25519',
        personalAccessToken: '',
        isActive: true
    });

    const { data: tenants = [] } = useTenants();
    const createSSHConnection = useCreateSSHConnection();
    const createPATConnection = useCreatePATConnection();
    const updateConnection = useUpdateGitHubConnection();
    const deleteConnection = useDeleteGitHubConnection();

    const isFormValid = formData.tenantId.trim().length > 0 &&
                       formData.name.trim().length > 0 &&
                       formData.repositoryUrl.trim().length > 0 &&
                       (formData.accessMethod !== 'pat' || formData.personalAccessToken.trim().length > 0);

    useEffect(() => {
        if (connection) {
            setFormData({
                tenantId: connection.tenantId,
                name: connection.name,
                description: connection.description || '',
                repositoryUrl: connection.repositoryUrl,
                accessMethod: connection.accessMethod,
                connectionType: connection.connectionType || 'repository',
                keyType: connection.keyType || 'ed25519',
                personalAccessToken: '',
                isActive: connection.isActive
            });
        }
    }, [connection]);

    useEffect(() => {
        if (setEditorState) {
            setEditorState({ saveEnabled: isFormValid });
        }
    }, [setEditorState, isFormValid]);

    const handleSave = useCallback(async () => {
        const { keyType, personalAccessToken, ...saveData } = formData;
        const connectionData = {
            ...saveData,
            keyType: formData.accessMethod === 'ssh' ? keyType : undefined,
            personalAccessToken: formData.accessMethod === 'pat' ? personalAccessToken : undefined
        };

        try {
            if (connection) {
                // Update existing connection
                await updateConnection.mutateAsync({
                    ...connectionData,
                    connectionId: `${connection.tenantId}-${connection.repositoryUrl}`
                });
            } else {
                // Create new connection based on access method
                if (formData.accessMethod === 'ssh') {
                    await createSSHConnection.mutateAsync(connectionData);
                } else if (formData.accessMethod === 'pat') {
                    await createPATConnection.mutateAsync({
                        ...connectionData,
                        personalAccessToken: personalAccessToken
                    });
                }
            }
            onSave?.(connectionData);
            onCancel();
        } catch (error) {
            console.error('Failed to save connection:', error);
        }
    }, [formData, connection, updateConnection, createSSHConnection, createPATConnection, onSave, onCancel]);

    const handleDelete = useCallback(async () => {
        if (connection) {
            try {
                const connectionId = `${connection.tenantId}-${connection.repositoryUrl}`;
                await deleteConnection.mutateAsync(connectionId);
                onCancel();
            } catch (error) {
                console.error('Failed to delete connection:', error);
            }
        }
    }, [connection, deleteConnection, onCancel]);

    useEffect(() => {
        if (editorActions) {
            editorActions.onSave = handleSave;
            editorActions.onDelete = connection ? handleDelete : undefined;
            editorActions.onCancel = onCancel;
        }
    }, [editorActions, onCancel, handleSave, handleDelete, connection]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth required>
                <InputLabel>Tenant</InputLabel>
                <Select
                    value={formData.tenantId}
                    label="Tenant"
                    onChange={(e) => setFormData({ ...formData, tenantId: e.target.value })}
                >
                    {tenants.map(tenant => (
                        <MenuItem key={tenant.id} value={tenant.id}>
                            {tenant.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <TextField
                label="Connection Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                fullWidth
                placeholder="My GitHub Connection"
                helperText="A friendly name for this connection"
            />

            <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
                placeholder="Description of this connection..."
                helperText="Optional description"
            />

            <FormControl fullWidth required>
                <InputLabel>Connection Type</InputLabel>
                <Select
                    value={formData.connectionType}
                    label="Connection Type"
                    onChange={(e) => setFormData({ ...formData, connectionType: e.target.value as 'repository' | 'organization' | 'user' })}
                >
                    <MenuItem value="repository">Repository</MenuItem>
                    <MenuItem value="organization">Organization</MenuItem>
                    <MenuItem value="user">User</MenuItem>
                </Select>
            </FormControl>

            <TextField
                label={formData.connectionType === 'repository' ? 'Repository URL' : formData.connectionType === 'organization' ? 'Organization URL' : 'User URL'}
                value={formData.repositoryUrl}
                onChange={(e) => setFormData({ ...formData, repositoryUrl: e.target.value })}
                required
                fullWidth
                placeholder={formData.connectionType === 'repository' ? 'https://github.com/owner/repository' : formData.connectionType === 'organization' ? 'https://github.com/organization' : 'https://github.com/username'}
                helperText={`GitHub ${formData.connectionType} URL`}
            />



            {formData.accessMethod === 'ssh' && (
                <>
                    <FormControl fullWidth>
                        <InputLabel>SSH Key Type</InputLabel>
                        <Select
                            value={formData.keyType}
                            label="SSH Key Type"
                            onChange={(e) => setFormData({ ...formData, keyType: e.target.value as 'rsa' | 'ed25519' })}
                            disabled={!!connection?.publicKey}
                        >
                            <MenuItem value="ed25519">ED25519 (Recommended)</MenuItem>
                            <MenuItem value="rsa">RSA 2048</MenuItem>
                        </Select>
                    </FormControl>
                    
                    <SSHKeyConfiguration
                        existingPublicKey={connection?.publicKey}
                        keyType={formData.keyType}
                        connectionId={connection ? `${connection.tenantId}-${connection.repositoryUrl}` : undefined}
                    />
                </>
            )}

            {formData.accessMethod === 'pat' && (
                <PATConfiguration
                    onTokenChange={(token) => setFormData({ ...formData, personalAccessToken: token })}
                    existingToken={formData.personalAccessToken}
                />
            )}






        </Box>
    );
};

export default GitHubConnectionEdit;